import { BrowserUtil } from "./BrowserUtil.js";

/**
 * Memory persistent store of JS objects for different contexts:
 *
 * - local: Local storage, persistent across sessions, current available in browser environment only (localStorage).
 * - session: Session storage, for one browser tab only, current available in browser environment only (sessionStorage). Later implementations may support session storage also on the server side as in user session.
 * - global: Singleton cache for the whole application. Uses globalThis in both browser and server environments. Note that server-side this is shared by all users of the application.
 *
 * The objects are stored as JSON strings in the session and local storage (must be JSON serializable).
 * The objects are stored directly as-is in "global" (can be any type).
 *
 * @param T The type of the value to store. Must be JSON serializable for local and session storage, can be any type for global storage.
 */
export class MemoryStorage<T extends Record<string, any> = Record<string, any>> {

  private readonly fullKey: string;

  /**
   * Creates a new MemoryStorage.
   * @param type The type of store to use. See description for possible values.
   * @param name Unique name of the store.
   */
  constructor(private readonly type: "local" | "session" | "global", readonly name: string) {
    this.fullKey = "salaxy." + name;
  }

  public get<T>(): T | undefined;
  public get<T>(defaultValue: T): T;
  /**
   * Gets a value from the store.
   * @param defaultValue The default value to return if the value is not found.
   * @returns The value from the store or the default value if the value is not found.
   */
  public get<T>(defaultValue?: T): T | undefined {
    if (this.type === "local" || this.type === "session") {
      const storage = this.getLocalOrSessionStorage();
      const value = storage.getItem(this.fullKey);
      if (value) {
        return JSON.parse(value) as T;
      }
      if (!defaultValue) {
        return undefined;
      }
      storage.setItem(this.fullKey, JSON.stringify(defaultValue));
      return defaultValue;
    }
    if (this.type === "global") {
      const value = (globalThis as any)[this.fullKey];
      if (!value && defaultValue) {
        (globalThis as any)[this.fullKey] = defaultValue;
        return defaultValue;
      }
      return value as T;
    }
    throw new Error("Invalid store type: " + this.type + " for key: " + this.fullKey);
  }

  /**
   * Gets a value from the store with default value as an asynchronous function that returns the default value.
   * Designed for situation where the value is fetched from a remote source and we want to cache the result.
   * @param defaultValue A function that returns the default value.
   * @returns The value from the store or the default value if the value is not found.
   */
  public async getAsync(defaultValue: () => Promise<T>): Promise<T> {
    const result = this.get<T>();
    if (result) {
      return result;
    }
    const value = await defaultValue();
    this.set(value);
    return value;
  }

  /**
   * Sets a value in the store.
   * @param value The value to set.
   */
  public set(value: T): void {
    if (this.type === "local" || this.type === "session") {
      const storage = this.getLocalOrSessionStorage();
      storage.setItem(this.fullKey, JSON.stringify(value));
      return;
    }
    if (this.type === "global") {
      (globalThis as any)[this.fullKey] = value;
      return;
    }
    throw new Error("Invalid store type: " + this.type + " for key: " + this.fullKey);
  }

  /**
   * Clears the value of the store (not the entire store, just value identified by the name).
   */
  public clear(): void {
    if (this.type === "local" || this.type === "session") {
      const storage = this.getLocalOrSessionStorage();
      storage.removeItem(this.fullKey);
      return;
    }
    if (this.type === "global") {
      (globalThis as any)[this.fullKey] = undefined;
      return;
    }
    throw new Error("Invalid store type: " + this.type + " for key: " + this.fullKey);
  }

  /**
   * Gets the local or session storage.
   * @returns The local or session storage.
   */
  private getLocalOrSessionStorage(): {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
  } {
    const storage = this.type === "local" ? BrowserUtil.getLocalStorage() : BrowserUtil.getSessionStorage();
    if (!storage) {
      throw new Error("Storage not available outside of browser environment: " + this.type);
    }
    return storage;
  }
}